import {useCallback, useEffect, useMemo} from "react";
import {HeroHeader} from "@/components/HeroHeader";
import LoadingGridItems from "@/components/LoadingGridItems";
import NewsGrid from "@/components/NewsGrid";
import {useQueryModal} from "@/hooks/useQueryModal";
import {useTableQuery} from "@/hooks/useTableQuery";
import {type SortField as CategorySortField, useCategories} from "@/hooks/useCategories";
import {useNewsByCategorySlug} from "@/hooks/useNews";
import {MetaTags} from "@/hooks/MetaTags.tsx";

export default function CategoriesSelectionPage() {
    const {searchParams, setParam} = useQueryModal();

    const {
        page,
        size,
        sortBy,
        sortOrder,
    } = useTableQuery<CategorySortField>({
        searchParams,
        setParam,
        defaultPage: 0,
        defaultSize: 50,
        defaultSortBy: "name",
        defaultSortOrder: "asc",
    });

    const newsPage = useMemo(
        () => Math.max(0, parseInt(searchParams.get("npage") ?? "0", 10) || 0),
        [searchParams]
    );

    const setNewsPage = useCallback(
        (next: number) => {
            setParam((p) => p.set("npage", String(Math.max(0, next))));
        },
        [setParam]
    );

    const {
        data: catResult = {page, size, total: 0, items: []} as any,
        isLoading: catsLoading,
        isError: catsError,
    } = useCategories({page, size, sortBy, sortOrder});

    const selectedSlug = searchParams.get("name") ?? undefined;

    useEffect(() => {
        if (!catsLoading && !catsError && !selectedSlug && catResult.items.length > 0) {
            const first = catResult.items[0];
            if (first?.slug) {
                setParam((p) => {
                    p.set("name", first.slug);
                    p.set("npage", "0");
                }, {replace: true});
            }
        }
    }, [catsLoading, catsError, selectedSlug, catResult.items, setParam]);

    const selectedCat = useMemo(
        () => catResult.items.find((c: any) => c.slug === selectedSlug),
        [catResult.items, selectedSlug]
    );

    const {
        data: news = {page: 0, size: 0, total: 0, totalPages: 0, items: []} as any,
        isLoading: newsLoading,
        isError: newsError,
    } = useNewsByCategorySlug(selectedSlug ?? "", {
        page: newsPage,
        size: 12,
        sortBy: "publishedAt",
        sortOrder: "desc",
    });

    const pickCategory = useCallback(
        (c: any) => {
            setParam((p) => {
                p.set("name", c.slug);
                p.set("npage", "0");
            });
        },
        [setParam]
    );

    if (catsLoading) return <LoadingGridItems/>;
    if (catsError) return <p className="mt-6 text-sm text-red-600">Kon nie kategorieë laai nie. Probeer asseblief
        weer.</p>;

    return (
        <>
            <MetaTags
                title={"Kosmos 94.1 - Kies Kategorieë"}
                description={"Soek berigte volgens kategorieë van jou keuse"}
                url={"/nuus/kategorieë"}
            />

            <HeroHeader
                title="Soek na 'n kategorie?"
                subtitle="Soek jy ‘n berig van ‘n spesifieke geur of kleur? Klik op enige van die kategorieë wat jou prikkel."
                imageAlt="kategorie"
            />

            <section className="py-16 sm:py-22">
                <div className="mx-auto max-w-7xl px-6 lg:px-8">

                    {/* Category grid */}
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                        {catResult.items.map((c: any) => {
                            const active = c.slug === selectedSlug;
                            return (
                                <button
                                    key={c.slug}
                                    type="button"
                                    onClick={() => pickCategory(c)}
                                    className={[
                                        "group flex h-20 items-center justify-center rounded-xl px-3 text-center transition",
                                        "ring-1",
                                        active
                                            ? "bg-indigo-50 ring-indigo-300 shadow-sm"
                                            : "bg-white ring-gray-200 hover:-translate-y-0.5 hover:shadow-md",
                                    ].join(" ")}
                                    aria-pressed={active}
                                    aria-label={`Kies kategorie ${c.name}`}
                                >
                                  <span
                                      className={[
                                          "line-clamp-2 text-sm font-medium",
                                          active ? "text-indigo-700" : "text-blue-900 group-hover:text-indigo-600",
                                      ].join(" ")}
                                  >
                                    {c.name}
                                  </span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Results */}
                    <div className="mx-auto mt-8 max-w-7xl">
                        {newsLoading ? (
                            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                                {Array.from({length: 6}).map((_, i) => (
                                    <div key={i} className="h-40 animate-pulse rounded-xl bg-gray-100"/>
                                ))}
                            </div>
                        ) : newsError ? (
                            <p className="text-sm text-red-600">Kon nie artikels laai nie.</p>
                        ) : news.items.length === 0 ? (
                            <div className="text-center">
                                <p className="text-sm text-gray-500">
                                    {selectedCat ? "Geen artikels in hierdie kategorie nie." : "Kies asseblief eers 'n kategorie."}
                                </p>
                            </div>
                        ) : (
                            <>
                                <NewsGrid
                                    content={news}
                                    seeMoreDisplay={false}
                                    showCategory={false}
                                    setPage={setNewsPage}
                                />
                            </>

                        )}
                    </div>
                </div>
            </section>
        </>
    );
}
